import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../../constants/routes';
import HomePageThumb from './HomePageThumb';

/**
 * Tolerant page-measurement join strategy.
 *
 * Replan stores per-assembly page measurements with keys shaped like
 * `${version}|${pageIndex}` where `pageIndex` is the 0-based page index (see
 * Replan.js `updateAssemblyMeasurements` and the `getPageIndexInVersion`
 * helper). We try the canonical 0-based key first, then derive the 0-based
 * key from the 1-based `actualPageNumber` as a strong fallback (so a missing
 * or misaligned `pageIndex` still hits). Legacy 1-based forms are kept at the
 * end in case older data was written before the format converged. Every
 * assembly that matches the page contributes a measurement entry, so a single
 * page can list measurements from multiple assemblies (Walls, Doors, etc.).
 */
const getCandidateKeys = (thumb) => {
  const keys = [];
  // 1) Canonical: version + 0-based pageIndex
  if (thumb.version != null && typeof thumb.pageIndex === 'number') {
    keys.push(`${thumb.version}|${thumb.pageIndex}`);
  }
  // 2) Strong fallback: derive 0-based from 1-based actualPageNumber
  if (thumb.version != null && typeof thumb.actualPageNumber === 'number') {
    keys.push(`${thumb.version}|${thumb.actualPageNumber - 1}`);
  }
  // 3) Legacy 1-based forms (in case older data was written with 1-based)
  if (thumb.version != null && typeof thumb.actualPageNumber === 'number') {
    keys.push(`${thumb.version}|${thumb.actualPageNumber}`);
    keys.push(`${thumb.version}/${thumb.actualPageNumber}`);
  }
  if (thumb.folderPath != null && typeof thumb.actualPageNumber === 'number') {
    keys.push(`${thumb.folderPath}/${thumb.actualPageNumber}`);
  }
  if (typeof thumb.actualPageNumber === 'number') {
    keys.push(String(thumb.actualPageNumber));
  }
  if (thumb.label) {
    keys.push(thumb.label);
  }
  return keys;
};

/**
 * Build one row per annotated page thumbnail. Each row carries the full list of
 * measurements from every assembly whose pageMeasurements match the page,
 * sorted by shapeCount descending then assemblyName ascending. Pages with no
 * matching measurements return an empty measurements array, which the UI
 * renders as a single "—" placeholder.
 */
const buildPageRows = (annotatedPages, assemblyData) => {
  const { thumbnails = [] } = annotatedPages || {};
  const assemblies = assemblyData || [];

  return thumbnails.map((thumb) => {
    const candidates = getCandidateKeys(thumb);
    const measurements = [];

    for (const asm of assemblies) {
      const pageMeasurements = asm.pageMeasurements || {};
      for (const key of candidates) {
        if (pageMeasurements[key] != null) {
          const pm = pageMeasurements[key];

          measurements.push({
            assemblyName: asm.name,
            displayValue: pm.displayValue ?? '—',
            unit: pm.unit ?? '',
            shapeCount: pm.shapeCount ?? 0,
            color: asm.params?.Color ?? null,
          });
          break; // first matching key per assembly; move to next assembly
        }
      }
    }

    // Largest measurement value first.
    measurements.sort((a, b) => {
      const valA = parseFloat(String(a.displayValue).replace(/,/g, '')) || 0;
      const valB = parseFloat(String(b.displayValue).replace(/,/g, '')) || 0;
      return valB - valA;
    });

    return { thumbnail: thumb, measurements };
  });
};

const PagesMeasurements = ({
  annotatedPages,
  annotatedLoading,
  assemblyData,
  assemblyLoading,
  selectedProject,
  selectedProjectDate,
  isOrgAdmin,
  onTakeoff,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const totalPages = annotatedPages?.total ?? 0;
  const annotatedCount = annotatedPages?.annotated ?? 0;
  const pct = totalPages > 0 ? Math.round((annotatedCount / totalPages) * 100) : 0;
  const dateLabel = selectedProjectDate ? selectedProjectDate.toLocaleDateString() : null;
  const thumbnails = annotatedPages?.thumbnails ?? [];

  const rows = useMemo(
    () => buildPageRows(annotatedPages, assemblyData),
    [annotatedPages, assemblyData],
  );

  const handlePageSelect = (thumb) => {
    if (!selectedProject || !thumb?.documentId) return;
    const params = new URLSearchParams();
    params.set('project', selectedProject);
    if (thumb.version) params.set('version', thumb.version);
    if (thumb.folderPath) params.set('folder', thumb.folderPath);
    if (thumb.label) params.set('page', thumb.label);
    navigate(`${ROUTES.REPLAN}?${params.toString()}`);
  };

  if (annotatedLoading || assemblyLoading) {
    return (
      <div className="glass metric-card">
        <h5>{t('home.pages_measurements_title')}</h5>
        <div className="placeholder-line" style={{ height: '0.5rem', width: '40%', marginBottom: '0.75rem' }} />
        <div className="home-pages-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="home-pages-list-item">
              <div className="home-pages-list-col-left">
                <div className="placeholder-line" style={{ height: '0.75rem', width: '70%' }} />
                <div className="home-pages-list-thumb">
                  <div className="placeholder-line" style={{ height: '240px', width: '100%' }} />
                </div>
              </div>
              <div className="home-pages-list-col-right">
                <div className="placeholder-line" style={{ height: '0.8rem', width: '80%' }} />
                <div className="placeholder-line" style={{ height: '0.8rem', width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (thumbnails.length === 0) {
    if (!isOrgAdmin) return null;
    return (
      <div className="glass metric-card">
        <h5>{t('home.pages_measurements_title')}</h5>
        <p className="home-chart-empty">
          {t('home.pages_no_annotated', { date: dateLabel ? ` (since ${dateLabel})` : '' })}
        </p>
      </div>
    );
  }

  return (
    <div className="glass metric-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h5 style={{ margin: 0 }}>{t('home.pages_measurements_title')}</h5>
        <span className="home-org-users-count">
          {t('home.pages_count', { annotated: annotatedCount, total: totalPages, pct })}
        </span>
      </div>
      <div className="home-annotation-progress" style={{ marginBottom: '0.75rem' }}>
        <div className="home-annotation-bar-track">
          <div className="home-annotation-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="home-pages-list">
        {rows.map((row, i) => {
          const { thumbnail, measurements } = row;
          const isNavigable = !!(selectedProject && thumbnail.documentId);

          return (
            <div
              key={`${thumbnail.documentId || 'no-doc'}-${thumbnail.actualPageNumber ?? i}-${thumbnail.pageIndex ?? i}-${thumbnail.label || i}`}
              className="home-pages-list-item"
            >
              <div className="home-pages-list-col-left">
                <div className="home-pages-list-label">
                  {thumbnail.label || `Pg ${(thumbnail.pageIndex ?? i) + 1}`}
                </div>
                <button
                  className="home-pages-list-thumb-btn"
                  type="button"
                  disabled={!isNavigable}
                  onClick={() => handlePageSelect(thumbnail)}
                  title={isNavigable ? `${thumbnail.label || ''} — open in Replan` : thumbnail.label || ''}
                >
                  <div className="home-pages-list-thumb">
                    <HomePageThumb
                      documentId={thumbnail.documentId}
                      actualPageNumber={thumbnail.actualPageNumber}
                      label={thumbnail.label}
                      pageIndex={thumbnail.pageIndex}
                    />
                  </div>
                </button>
              </div>
              <div className="home-pages-list-col-right">
                {measurements.length === 0 ? (
                  <div className="home-pages-list-empty">—</div>
                ) : (
                  <ul className="home-pages-list-measurements">
                    {measurements.map((m) => (
                      <li
                        key={m.assemblyName}
                        className="home-pages-list-measurement-item"
                        style={m.color ? { borderLeft: `4px solid ${m.color}` } : undefined}
                      >
                        <span className="home-pages-list-asm-name">
                          {m.assemblyName}
                        </span>
                        <span className="home-pages-list-measurement-detail">
                          {parseFloat(String(m.displayValue).replace(/,/g, '')) > 0 && <span className="home-assembly-value">{m.displayValue}</span>}
                          {parseFloat(String(m.displayValue).replace(/,/g, '')) > 0 && m.unit && <span className="home-assembly-unit">{m.unit}</span>}
                          <span className="home-pages-list-shape-count">{t('home.assembly_totals_shapes', { count: m.shapeCount })}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {onTakeoff && (
        <div className="home-pages-footer">
          <button className="home-assembly-link" onClick={onTakeoff}>
            {t('home.pages_takeoff')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PagesMeasurements;
